import { loginState, SelectedOnboardingAction } from 'global-stores';
import { useFirebaseInit } from 'hooks/firebase';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { ROUTES } from 'routes';
import {
	acquireOnboardingLock,
	releaseOnboardingLock,
	setOnboardingLockOnDisconnect,
	updateOnboardingLockHeartbeat,
} from 'utils/onboardingLock';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 min inactivity before warning
const MODAL_COUNTDOWN_SEC = 5 * 60; // 5 min countdown in modal

export const useOnboardingLock = () => {
	const user = useRecoilValue(loginState);
	const selectedOnboarding = useRecoilValue(SelectedOnboardingAction);

	const { database } = useFirebaseInit();
	const navigator = useNavigate();

	const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
	const [countdown, setCountdown] = useState(MODAL_COUNTDOWN_SEC);

	const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
	const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
	const lockHeartbeatRef = useRef<NodeJS.Timeout | null>(null);

	// Reset inactivity timer on user activity
	const resetInactivityTimer = useCallback(() => {
		if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
		if (inactiveModalOpen) return; // Don't reset if modal is open
		inactivityTimerRef.current = setTimeout(() => {
			setInactiveModalOpen(true);
			setCountdown(MODAL_COUNTDOWN_SEC);
		}, INACTIVITY_TIMEOUT_MS);
	}, [inactiveModalOpen]);

	// Continue button handler
	const handleContinueActivity = useCallback(() => {
		setInactiveModalOpen(false);
		setCountdown(MODAL_COUNTDOWN_SEC);
		resetInactivityTimer();
	}, [resetInactivityTimer]);

	const handleLeave = useCallback(() => {
		setInactiveModalOpen(false);
		releaseOnboardingLock(database, selectedOnboarding.id, user.userId || '');
		navigator(ROUTES.PIPELINE);
	}, [database, navigator, selectedOnboarding.id, user.userId]);

	// Acquire lock on mount
	useEffect(() => {
		if (selectedOnboarding.type !== 'modify') return; // Only acquire lock for onboarding flows
		let isMounted = true;
		(async () => {
			const { success } = await acquireOnboardingLock(
				database,
				selectedOnboarding.id,
				user
			);
			if (!isMounted) return;
			if (success) {
				setOnboardingLockOnDisconnect(database, selectedOnboarding.id);
				// Heartbeat every 2 min
				lockHeartbeatRef.current = setInterval(() => {
					updateOnboardingLockHeartbeat(database, selectedOnboarding.id, user);
				}, 2 * 60 * 1000);
			}
		})();
		return () => {
			isMounted = false;
			if (lockHeartbeatRef.current) clearInterval(lockHeartbeatRef.current);
			releaseOnboardingLock(database, selectedOnboarding.id, user.userId || '');
		};
	}, [database, selectedOnboarding, user]);

	// Listen for user activity
	useEffect(() => {
		const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'focus'];
		const handler = () => resetInactivityTimer();
		events.forEach(e => window.addEventListener(e, handler));
		resetInactivityTimer();
		return () => {
			events.forEach(e => window.removeEventListener(e, handler));
			if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
		};
	}, [resetInactivityTimer]);

	// Countdown logic for modal
	useEffect(() => {
		if (!inactiveModalOpen) return;
		if (countdown <= 0) {
			handleLeave();
			return;
		}
		countdownTimerRef.current = setTimeout(() => {
			setCountdown(c => c - 1);
		}, 1000);
		return () => {
			if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
		};
	}, [
		inactiveModalOpen,
		countdown,
		database,
		selectedOnboarding.id,
		user.userId,
		handleLeave,
	]);

	return {
		handleContinueActivity,
		handleLeave,
		inactiveModalOpen,
		countdown,
	};
};
